import React, {useState} from 'react';

import {HomeActionsEnum} from 'main/analytics/gaTagsEnum';
import {StyledGrid} from 'presentation/components';
import Tutorial from 'presentation/components/Tutorial';
import {AuthorizationsEnum, GuidesEnum, ServicesEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useContractStatus} from 'presentation/hooks/useContractStatus';
import {ppoNetworkTypeEnum} from 'presentation/modules/guide/shared/store/guideStore/types';
import {usePpoNetworkStore} from 'presentation/modules/guide/shared/store/guideStore/usePpoNetworkStore';
import {ShortcutWarningDrawer} from 'presentation/pages/private/booking/components/ShortcutWarningDrawer';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {useServicesListForShortcuts} from 'presentation/pages/private/hooks/useServicesListForShortcuts';
import {ServicesTypesEnum} from 'presentation/pages/private/services/types';
import {useSideBarOpenStateStore} from 'presentation/pages/private/store/sideBarOpenStateStore';
import {useOpenOptionsDrawerStore} from 'presentation/pages/private/store/useOpenOptionsDrawer';
import {translations} from 'presentation/translations-new';
import {Placement} from 'react-joyride';
import {useNavigate} from 'react-router-dom';
import {useAccreditedNetworkItems} from '../OptionsDrawer/hooks/useAccreditedNetworkItems';
import {useAuthorizationItems} from '../OptionsDrawer/hooks/useAuthorizationsItems';
import {useTelemedicineItems} from '../OptionsDrawer/hooks/useTelemedicineItems';
import {Shortcut} from '../Shortcut';
import {ShortcutListType} from './type';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

const {seeAll} = translations['pt-br'].commons;
const {authorizations, telemedicine, accreditedNetwork} =
	translations['pt-br'].privateFlow.home.portalShortcutLabels;
const {shortcutList: favoriteDoctors} = translations['pt-br'].privateFlow.guideFlow.favoriteDoctors;

export function ShortcutList({
	isHome,
	query,
	isTutorialBlocked,
	onTutorialDismiss,
}: ShortcutListType) {
	const navigate = useNavigate();

	const setPpoNetworkType = usePpoNetworkStore(state => state.setPpoNetworkType);

	const {SHORTCUTS_LIST} = useServicesListForShortcuts(query);
	const isSideBarOpenState = useSideBarOpenStateStore(state => state.sideBarOpenState);
	const {isEarlyAccess, healthOnly, odontoOnly} = useAccountInformation();
	const {isNotredameSaude} = useHostName();
	const [warningDrawerContainerState, setWarningDrawerContainerState] = useState<
		undefined | string
	>();
	const congenereEligibility = useCongenereEligibilityStore(state => state.congenereEligibility);
	const {isValid} = useContractStatus();
	const {openOptionsDrawer} = useOpenOptionsDrawerStore();
	const {items: telemedicineItems} = useTelemedicineItems();
	const {items: authorizationsItems} = useAuthorizationItems();
	const {items: accreditedNetworkItems} = useAccreditedNetworkItems();

	const isPortalLayout = isHome && isNotredameSaude;
	const showSeeAllServices =
		isHome && isValid && !isEarlyAccess && !isPortalLayout && !congenereEligibility;
	const mdGridSize = isHome || isSideBarOpenState ? 4 : 3;
	const xlGridSize = isHome ? 3 : 2.4;

	const shortcutsToDisplay = SHORTCUTS_LIST;

	const lgGridSize = () => {
		if (isPortalLayout) {
			return 3;
		}

		if (isHome) {
			return isSideBarOpenState ? 4 : 3;
		}

		return 2.4;
	};

	const overrideClick = (title: string) => {
		if (title === telemedicine) {
			return openOptionsDrawer(telemedicine, telemedicineItems);
		}

		if (title === authorizations) {
			if (healthOnly) {
				return navigate(AuthorizationsEnum.AUTHORIZATIONS);
			}

			if (odontoOnly) {
				return navigate(AuthorizationsEnum.AUTHORIZATIONS_ODONTO);
			}

			return openOptionsDrawer(authorizations, authorizationsItems);
		}

		if (title === accreditedNetwork) {
			if (healthOnly) {
				if (isHome && isNotredameSaude) {
					setPpoNetworkType(ppoNetworkTypeEnum.ACCREDITED_NETWORK);
				}
				return navigate(GuidesEnum.SERVICE_NETWORK_HEALTH);
			}

			if (odontoOnly) {
				return navigate(GuidesEnum.SERVICE_NETWORK_ODONTO);
			}

			return openOptionsDrawer(accreditedNetwork, accreditedNetworkItems);
		}

		return undefined;
	};

	return (
		<StyledGrid.Root spacing={1} maxWidth={isHome ? undefined : 1288}>
			{shortcutsToDisplay.map(shortcut => {
				return (
					<StyledGrid.Item
						key={shortcut.title}
						xs={6}
						sm={isPortalLayout ? 6 : 4}
						md={isPortalLayout ? 3 : mdGridSize}
						lg={lgGridSize()}
						xl={isPortalLayout ? 3 : xlGridSize}>
						<Shortcut
							dataTagId={shortcut?.dataTagId}
							isInversedColor={shortcut.isInversedColor}
							icon={shortcut.icon}
							title={shortcut.title}
							route={shortcut.route}
							isBlob={shortcut.isBlob}
							endpoint={shortcut.endpoint}
							featureFlagName={shortcut.featureFlag}
							type={shortcut.type}
							handleClick={shortcut.handleClick}
							warningDrawerContent={shortcut.warningDrawerContent}
							disableCache={shortcut?.disableCache}
							setWarningDrawerContainer={() =>
								setWarningDrawerContainerState(shortcut.title)
							}
							id={shortcut?.id}
							layoutVariant={isPortalLayout ? 'portal' : 'default'}
							overrideClick={
								shortcut.title === telemedicine ||
								shortcut.title === authorizations ||
								shortcut.title === accreditedNetwork
									? () => overrideClick(shortcut.title)
									: undefined
							}
						/>
					</StyledGrid.Item>
				);
			})}
			{showSeeAllServices ? (
				<StyledGrid.Item
					xs={6}
					sm={4}
					md={isHome || isSideBarOpenState ? 4 : 3}
					lg={lgGridSize()}
					xl={3}>
					<Shortcut
						dataTagId={HomeActionsEnum.CLICK_VER_TODOS_MENU_PRINCIPAL}
						icon="more-horiz-filled"
						title={seeAll}
						route={ServicesEnum.SERVICES}
						type={ServicesTypesEnum.OTHERS}
					/>
				</StyledGrid.Item>
			) : null}
			<ShortcutWarningDrawer
				title={warningDrawerContainerState || ''}
				open={!!warningDrawerContainerState}
				onClose={() => setWarningDrawerContainerState(undefined)}
			/>
			{isTutorialBlocked ? null : (
				<Tutorial
					steps={[
						{
							target: '#favoritesShortcut',
							content: favoriteDoctors.tutorial,
							placement: 'bottom' as Placement,
							disableBeacon: true,
						},
					]}
					storageKey="favoritesShortcut"
					onComplete={onTutorialDismiss}
					onClose={onTutorialDismiss}
					onSecondaryAction={onTutorialDismiss}
				/>
			)}
		</StyledGrid.Root>
	);
}
